import { z } from "zod";

// Reference: which widget instance + state path the constraint reads.
// dot-notation path, e.g., "selectedFlavors" or "batches[0].multiplier"
// Open question A4 resolved: Phase 1 commits to dot-notation strings.
// Phase 9 evaluator may add optional pathSyntax: 'dot' | 'jsonpath' additively (v2).

// isolatedDeclarations + verbatimModuleSyntax: explicit interface for the type,
// z.ZodType<T> annotation for the const. No re-export type alias needed.

// REVIEWS-3 HIGH-2: Predicate AST composition uses z.union (not z.discriminatedUnion).
// REVIEWS-3 MEDIUM-13: countPredicateNodes is iterative.
// REVIEWS-4 HIGH-2: checkPredicateDepthPrewalk is iterative raw-input prewalk running BEFORE Zod's safeParse — defends against z.lazy structural-parse V8 stack overflow.
export interface StateRef {
  widgetInstanceId: string;
  pathInState: string;
}

const _stateRefSchema = z.strictObject({
  widgetInstanceId: z.string().min(1),
  pathInState: z.string().min(1),
});

export const StateRef: z.ZodType<StateRef> = _stateRefSchema;

// Cardinality constraint: limits how many of a resource may exist in a scope.
// Examples: max 1 recipe per spread; min 1 / max 3 flavor options per widget.
const _cardinalitySchema = z.strictObject({
  kind: z.literal("cardinality"),
  resource: z.enum(["recipe-in-spread", "option-in-widget"]),
  scope: z.string().min(1),
  min: z.number().int().nonnegative().optional(),
  max: z.number().int().positive(),
  explanation: z.string().min(1).max(500), // CSTR-05: text is data, not hardcoded
});

// Propagation constraint: when a trigger fires, apply effect to batch sets.
// CSTR-06: an exclusive recipe is selectable only in one spread and propagates to all batch sets.
const _propagationSchema = z.strictObject({
  kind: z.literal("propagation"),
  trigger: _stateRefSchema,
  effect: z.enum([
    "mirror-all-batch-sets",
    "lock-to-batch-set-zero",
    "inherit-from-batch-set-zero",
  ]),
  explanation: z.string().min(1).max(500), // CSTR-05
});

// Visibility constraint: show or hide a widget instance based on another widget's state.
// Example: banana-variant widget is visible when flavor-picker selectedFlavors equals 'banana'. // grep-allow: flavor-picker, banana-variant
const _visibilitySchema = z.strictObject({
  kind: z.literal("visibility"),
  show: _stateRefSchema,
  when: z.strictObject({
    ref: _stateRefSchema,
    equals: z.unknown(), // value to match — may be string, number, boolean, or object
  }),
  explanation: z.string().min(1).max(500), // CSTR-05
});

// Dependency constraint: require a state path to be set if a condition holds.
// Example: a long-fermentation recipe may require a preferment-status state to be set before mixing.
const _dependencySchema = z.strictObject({
  kind: z.literal("dependency"),
  if: z.strictObject({
    ref: _stateRefSchema,
    equals: z.unknown(), // condition value — any JSON value
  }),
  require: _stateRefSchema,
  explanation: z.string().min(1).max(500), // CSTR-05
});

// =============================================================================
// Selectability constraint (D-20 5th kind) — Predicate AST
// =============================================================================

// REVIEWS HIGH-1 — Variable type families (3 families: boolean / numeric / string).
// Closed variable enums prevent expressive creep per RESEARCH §Pitfall 3.
const BooleanVariable = z.enum(["batch-set-empty", "other-batch-sets-all-empty"]);
const NumericVariable = z.enum(["batch-set-position", "spread-recipe-count"]);
const StringVariable = z.enum(["active-mode-id"]);

// REVIEWS HIGH-1 — Per-variable-family Leaf schemas.
// Each family is a strictObject that binds variable enum + compatible operators.
const _booleanLeafSchema = z.strictObject({
  kind: z.literal("leaf"),
  variable: BooleanVariable,
  operator: z.enum(["is-true", "is-false"]),
  // NOTE: no `value` key — boolean variables have no operand value (strictObject rejects extra keys)
});

const _numericLeafSchema = z.strictObject({
  kind: z.literal("leaf"),
  variable: NumericVariable,
  operator: z.enum(["eq", "ne", "lt", "lte", "gt", "gte"]),
  value: z.number(),
});

const _stringLeafSchema = z.strictObject({
  kind: z.literal("leaf"),
  variable: StringVariable,
  operator: z.enum(["eq", "ne"]),
  value: z.string().min(1).max(100),
});

// REVIEWS-3 HIGH-2 (HIGH): z.union + .superRefine is the PRIMARY implementation.
// z.discriminatedUnion("variable", ...) is rejected because Zod 4 requires the discriminator to be
// z.literal() per variant; z.enum(...) variants do not satisfy this. The structural object parse per
// variant already enforces (variable, operator, value) compatibility — superRefine has no additional
// work but is left as a hook for future cross-leaf checks.
const LeafPredicateSchema = z
  .union([_booleanLeafSchema, _numericLeafSchema, _stringLeafSchema])
  .superRefine((_leaf, _ctx) => {
    // The structural object parse already enforced family/operator/value compatibility per variant.
    // No additional refinement needed — the union itself is the dispatch.
    // (Empty body kept explicit so future per-leaf cross-checks have a hook.)
  });

// Recursive Predicate type — discriminated union over leaf / and / or.
// Declared explicitly for isolatedDeclarations + exactOptionalPropertyTypes compatibility.
export type Predicate =
  | {
      kind: "leaf";
      variable: "batch-set-empty" | "other-batch-sets-all-empty";
      operator: "is-true" | "is-false";
    }
  | {
      kind: "leaf";
      variable: "batch-set-position" | "spread-recipe-count";
      operator: "eq" | "ne" | "lt" | "lte" | "gt" | "gte";
      value: number;
    }
  | {
      kind: "leaf";
      variable: "active-mode-id";
      operator: "eq" | "ne";
      value: string;
    }
  | { kind: "and"; predicates: readonly Predicate[] }
  | { kind: "or"; predicates: readonly Predicate[] };

// REVIEWS-3 HIGH-2: outer composition uses z.union([leafUnion, andSchema, orSchema]).
// z.discriminatedUnion("kind", [LeafPredicateSchema, andSchema, orSchema]) FAILS because
// LeafPredicateSchema is itself a z.union, not a plain object schema with z.literal("kind").
// z.union accepts any schemas; runtime dispatch is structural matching (Zod tries each variant
// and picks the first match — fails by default if none match).
const _rawPredicateSchema: z.ZodType<Predicate> = z.lazy(() =>
  z.union([
    LeafPredicateSchema,
    z.strictObject({
      kind: z.literal("and"),
      predicates: z.array(_rawPredicateSchema).min(1).max(10),
    }),
    z.strictObject({
      kind: z.literal("or"),
      predicates: z.array(_rawPredicateSchema).min(1).max(10),
    }),
  ]),
) as z.ZodType<Predicate>;

// REVIEWS-3 MEDIUM-13 (MEDIUM): ITERATIVE walk via worklist queue prevents V8 stack overflow on
// the AST node-count check during superRefine. countPredicateNodes does NOT defend against z.lazy
// structural-parse recursion (that's REVIEWS-4 HIGH-2's prewalk job).
function countPredicateNodes(root: unknown): number {
  // Pre-check: non-object inputs are 0 nodes (defensive — Zod will reject them anyway).
  if (root === null || typeof root !== "object" || !("kind" in root)) return 0;

  let count = 0;
  const queue: unknown[] = [root];
  while (queue.length > 0) {
    const node = queue.shift();
    if (node === null || typeof node !== "object" || !("kind" in node)) continue;

    count++;
    // Defensive early-exit: if we've already exceeded the cap, stop walking.
    // (Don't throw — return a value the superRefine compares against the cap.)
    if (count > 100) return count; // signals "over the cap" — caller compares > 100.

    const k = (node as { kind: string }).kind;
    if (k === "and" || k === "or") {
      const children = (node as { predicates?: unknown[] }).predicates;
      if (Array.isArray(children)) queue.push(...children);
    }
    // leaf nodes have no children — nothing to push.
  }
  return count;
}

// REVIEWS-4 HIGH-2 (HIGH): iterative raw-input depth prewalk runs BEFORE Zod's safeParse.
// Zod's z.lazy structural recursion crashes V8 at ~10K-50K depth depending on platform; this
// prewalk rejects > 8-deep inputs BEFORE Zod sees them. countPredicateNodes still handles AST
// node-count bounds inside superRefine (its role is unchanged).
export function checkPredicateDepthPrewalk(
  input: unknown,
  maxDepth: number = 8,
): { ok: true } | { ok: false; reason: string } {
  // Pre-check: non-object inputs are trivially OK (Zod will reject them downstream).
  if (input === null || typeof input !== "object" || !("kind" in input)) return { ok: true };

  // Worklist queue of [node, depthOfNode] tuples. Initial node is at depth 0.
  const queue: Array<[unknown, number]> = [[input, 0]];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) continue;
    const [node, depth] = next;
    if (node === null || typeof node !== "object" || !("kind" in node)) continue;

    if (depth > maxDepth) {
      return {
        ok: false,
        reason: `predicate depth exceeds maximum of ${maxDepth} (REVIEWS-4 HIGH-2 raw-input prewalk)`,
      };
    }

    const k = (node as { kind: string }).kind;
    if (k === "and" || k === "or") {
      const children = (node as { predicates?: unknown[] }).predicates;
      if (Array.isArray(children)) {
        for (const child of children) {
          queue.push([child, depth + 1]);
        }
      }
    }
    // leaf nodes have no children.
  }
  return { ok: true };
}

// Public PredicateSchema with depth-cap + node-cap superRefine.
// The prewalk (checkPredicateDepthPrewalk) is the pre-Zod perimeter defense.
// This superRefine is the in-AST defense for inputs that survived the prewalk + structural parse.
export const PredicateSchema: z.ZodType<Predicate> = _rawPredicateSchema.superRefine((p, ctx) => {
  // Depth cap ≤ 8 — recursive helper is bounded because the prewalk + breadth/node caps constrain inputs.
  const depth = (q: Predicate): number =>
    q.kind === "leaf" ? 0 : 1 + Math.max(0, ...q.predicates.map(depth));
  if (depth(p) > 8) {
    ctx.addIssue({
      code: "custom",
      message: "Predicate AST depth must be ≤ 8 (RESEARCH §Pitfall 3 bounded composition)",
    });
  }
  // REVIEWS-3 MEDIUM-13 NODE CAP via ITERATIVE walk.
  if (countPredicateNodes(p) > 100) {
    ctx.addIssue({
      code: "custom",
      message: "Predicate AST total node count must be ≤ 100 (REVIEWS HIGH-1 closure)",
    });
  }
});

// Selectability constraint (D-20 5th kind): controls whether a recipe is selectable
// in a spread based on runtime state. Phase 9 commits the evaluator.
const _selectabilitySchema = z.strictObject({
  kind: z.literal("selectability"),
  recipeId: z.string().min(1),
  predicate: PredicateSchema,
  explanation: z.string().min(1).max(500),
});

// Discriminated union — O(1) dispatch on 'kind' (RESEARCH Pattern 2 + Anti-Patterns).
// z.discriminatedUnion gives correct JSON Schema emission and better errors than z.union.
// NOTE: The OUTER Constraint union remains z.discriminatedUnion because all 5 variants share
// kind: z.literal(...) as the discriminator. REVIEWS-3 HIGH-2 only affects the INNER Predicate
// composition (where LeafPredicateSchema is a z.union, not a plain object schema).
const _constraintSchema = z.discriminatedUnion("kind", [
  _cardinalitySchema,
  _propagationSchema,
  _visibilitySchema,
  _dependencySchema,
  _selectabilitySchema, // NEW — 5th kind (D-20)
]);

// Explicit output type for isolatedDeclarations + exactOptionalPropertyTypes compatibility.
// exactOptionalPropertyTypes requires "min?: number | undefined" not "min?: number"
// to match Zod's inferred type (z.optional() produces T | undefined in the value).
export type Constraint =
  | {
      kind: "cardinality";
      resource: "recipe-in-spread" | "option-in-widget";
      scope: string;
      min?: number | undefined;
      max: number;
      explanation: string;
    }
  | {
      kind: "propagation";
      trigger: StateRef;
      effect: "mirror-all-batch-sets" | "lock-to-batch-set-zero" | "inherit-from-batch-set-zero";
      explanation: string;
    }
  | {
      kind: "visibility";
      show: StateRef;
      when: { ref: StateRef; equals: unknown };
      explanation: string;
    }
  | {
      kind: "dependency";
      if: { ref: StateRef; equals: unknown };
      require: StateRef;
      explanation: string;
    }
  | {
      kind: "selectability";
      recipeId: string;
      predicate: Predicate;
      explanation: string;
    };

export const Constraint: z.ZodType<Constraint> = _constraintSchema;
